/**
 * Snooze state for the "your company address is incomplete" banner.
 *
 * Same shape as `oldDashboardBannerState.ts` — `localStorage` for persistence
 * plus a custom `window` event so every subscriber in the tab re-renders
 * together — but the dismissal expires: the address is required, so the banner
 * comes back a day later until it is filled in. Keyed by user and company, so
 * snoozing for one company does not hide the reminder for another.
 */

const KEY_PREFIX = 'retraced:addressBannerSnoozedUntil';
const EVENT = 'retraced:addressBannerSnooze';

export const ADDRESS_BANNER_SNOOZE_MS = 24 * 60 * 60 * 1000;

function storageKey(userId: string, companyId: string): string {
  return `${KEY_PREFIX}:${userId}:${companyId}`;
}

export function isAddressBannerSnoozed(
  userId: string,
  companyId: string,
  now: number = Date.now(),
): boolean {
  try {
    const until = Number(localStorage.getItem(storageKey(userId, companyId)));
    return Number.isFinite(until) && until > now;
  } catch {
    // Private mode / storage disabled — show the banner rather than crash.
    return false;
  }
}

export function snoozeAddressBanner(
  userId: string,
  companyId: string,
  now: number = Date.now(),
): void {
  try {
    localStorage.setItem(
      storageKey(userId, companyId),
      String(now + ADDRESS_BANNER_SNOOZE_MS),
    );
  } catch {
    // Ignore: without storage the banner simply returns on the next load.
  }
  window.dispatchEvent(new Event(EVENT));
}

export function subscribeAddressBannerSnooze(onChange: () => void): () => void {
  window.addEventListener(EVENT, onChange);
  return () => window.removeEventListener(EVENT, onChange);
}
